import { useEffect, useState } from "react";
import { Save, ShieldCheck, KeyRound, Link2, HardDrive, FolderCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { useAppConfig } from "@/context/ConfigContext";
import { PageHeader } from "@/pages/Dashboard";
import { saveConfig } from "@/lib/api";
import { getModelsDir, setModelsDir } from "@/lib/agent";
import { FolderPickerButton } from "@/components/FolderPickerButton";
import { AgentSetupCard } from "@/components/AgentSetupCard";
import { toast } from "sonner";

export default function Settings() {
  const { config, setConfig, refreshConfig, refreshStatus, refreshModels } = useAppConfig();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dir, setDir] = useState({ loading: true, path: "", input: "", info: null, error: null, saving: false });

  useEffect(() => {
    (async () => {
      try {
        const d = await getModelsDir();
        if (d?.ok) setDir((s) => ({ ...s, loading: false, path: d.path || "", input: d.path || "", info: d, error: null }));
        else setDir((s) => ({ ...s, loading: false, error: d?.error || "No se pudo leer la carpeta de modelos del Agent." }));
      } catch (e) {
        setDir((s) => ({ ...s, loading: false, error: e?.message || "El Agent local no respondió. Conéctalo desde el Dashboard." }));
      }
    })();
  }, []);

  const saveDir = async () => {
    const path = dir.input.trim();
    if (!path) { toast.error("Indica la ruta de la carpeta"); return; }
    setDir((s) => ({ ...s, saving: true, error: null }));
    try {
      const d = await setModelsDir(path);
      if (d?.ok) {
        setDir((s) => ({ ...s, saving: false, path: d.path || path, info: d, error: null }));
        toast.success("Carpeta de modelos guardada");
        await refreshModels();
      } else {
        setDir((s) => ({ ...s, saving: false, error: d?.error || "No se pudo guardar la carpeta" }));
        toast.error(d?.error || "No se pudo guardar la carpeta");
      }
    } catch (e) {
      setDir((s) => ({ ...s, saving: false, error: e?.message || "El Agent local no respondió" }));
      toast.error(e?.message || "Error al guardar la carpeta");
    }
  };


  useEffect(() => {
    if (config) {
      setForm({
        base_url: config.base_url,
        timeout: config.timeout,
        default_model: config.default_model,
        default_temperature: config.default_temperature,
        default_max_tokens: config.default_max_tokens,
      });
    }
  }, [config]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const data = await saveConfig({
        base_url: form.base_url,
        timeout: Number(form.timeout),
        default_model: form.default_model,
        default_temperature: Number(form.default_temperature),
        default_max_tokens: Number(form.default_max_tokens),
      });
      if (data.ok) {
        setConfig(data.config);
        await refreshConfig();
        await refreshStatus();
        await refreshModels();
        toast.success("Configuración guardada");
      } else {
        toast.error(data.error || "No se pudo guardar la configuración");
      }
    } catch (e) {
      toast.error(e?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div className="krai-enter max-w-3xl">
      <PageHeader
        title="Configuración"
        subtitle="Ajustes del motor local · los valores se guardan de forma persistente"
        action={
          <Button data-testid="save-config-button" onClick={save} disabled={saving} className="rounded-sm bg-white text-black hover:bg-neutral-200">
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        }
      />

      <div className="space-y-4">
        <section className="rounded-md border border-[#262626] bg-[#121212] p-5">
          <div className="mb-4 flex items-center gap-2 text-[#737373]">
            <Link2 className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-widest">Conexión</span>
          </div>

          <Field label="Local AI Base URL" hint="Endpoint OpenAI-compatible (llama-server / llama.cpp)">
            <Input
              data-testid="config-base-url"
              value={form.base_url}
              onChange={(e) => update("base_url", e.target.value)}
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>

          <Field label="Timeout (segundos)">
            <Input
              data-testid="config-timeout"
              type="number"
              min={1}
              value={form.timeout}
              onChange={(e) => update("timeout", e.target.value)}
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>
        </section>

        <section className="rounded-md border border-[#262626] bg-[#121212] p-5">
          <div className="mb-4 flex items-center gap-2 text-[#737373]">
            <span className="font-mono text-[11px] uppercase tracking-widest">Valores por defecto</span>
          </div>

          <Field label="Modelo por defecto" hint="ID de un modelo detectado (opcional)">
            <Input
              data-testid="config-default-model"
              value={form.default_model}
              onChange={(e) => update("default_model", e.target.value)}
              placeholder="p. ej. gemma-3"
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>

          <Field label={`Temperatura por defecto · ${Number(form.default_temperature).toFixed(2)}`}>
            <Slider
              data-testid="config-default-temperature"
              value={[Number(form.default_temperature)]}
              min={0}
              max={2}
              step={0.05}
              onValueChange={(v) => update("default_temperature", v[0])}
            />
          </Field>

          <Field label="Max tokens por defecto">
            <Input
              data-testid="config-default-max-tokens"
              type="number"
              min={1}
              value={form.default_max_tokens}
              onChange={(e) => update("default_max_tokens", e.target.value)}
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>
        </section>

        <section className="rounded-md border border-[#262626] bg-[#121212] p-5" data-testid="agent-install-section">
          <AgentSetupCard title="Instalar / actualizar agente" reason="Instala o actualiza el agente en tu Mac. Solo hace falta una vez; después arranca solo al iniciar sesión." />
        </section>

        <section className="rounded-md border border-[#262626] bg-[#121212] p-5" data-testid="models-dir-section">
          <div className="mb-4 flex items-center gap-2 text-[#737373]">
            <HardDrive className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-widest">Carpeta de modelos</span>
          </div>

          <Field
            label="Ruta de descarga en tu Mac"
            hint="Ruta absoluta de la carpeta (por ejemplo un disco externo). El Agent descargará aquí los modelos."
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                data-testid="config-models-dir"
                value={dir.input}
                onChange={(e) => setDir((s) => ({ ...s, input: e.target.value }))}
                placeholder="/Volumes/MiDisco/krai-models"
                className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
              />
              <FolderPickerButton testid="settings-pick-folder" onPicked={(p) => setDir((s) => ({ ...s, input: p }))} />
              <Button
                data-testid="save-models-dir"
                onClick={saveDir}
                disabled={dir.saving || dir.loading || !dir.input.trim()}
                className="shrink-0 rounded-sm bg-white text-black hover:bg-neutral-200"
              >
                <FolderCheck className="mr-2 h-4 w-4" />
                {dir.saving ? "Guardando…" : "Usar carpeta"}
              </Button>
            </div>
          </Field>

          {dir.loading && <p className="font-mono text-xs text-[#737373]">Consultando al Agent local…</p>}

          {dir.info?.path && (
            <div className="mt-1 rounded-sm border border-[#262626] bg-[#0a0a0a] px-3 py-2.5" data-testid="models-dir-info">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px]">
                <span className="text-[#737373]">Carpeta activa:</span>
                <span className="text-[#f5f5f5]">{dir.info.path}</span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-2">
                <span className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] ${dir.info.exists ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>
                  {dir.info.exists ? "existe" : "no existe"}
                </span>
                <span className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] ${dir.info.writable ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>
                  {dir.info.writable ? "escribible" : "sin permiso de escritura"}
                </span>
                {typeof dir.info.free_gb === "number" && (
                  <span className="rounded-sm border border-[#404040] px-2 py-0.5 font-mono text-[10px] text-[#a3a3a3]">
                    libre {dir.info.free_gb} GB{typeof dir.info.total_gb === "number" ? ` / ${dir.info.total_gb} GB` : ""}
                  </span>
                )}
              </div>
            </div>
          )}

          {dir.error && (
            <div className="mt-1 flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2.5" data-testid="models-dir-error">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <div className="font-mono text-[11px] text-amber-200">{dir.error}</div>
            </div>
          )}
        </section>

        <section className="rounded-md border border-[#262626] bg-[#121212] p-5">
          <div className="mb-4 flex items-center gap-2 text-[#737373]">
            <KeyRound className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-widest">Seguridad</span>
          </div>

          <div className="flex items-center justify-between rounded-sm border border-[#262626] bg-[#0a0a0a] px-3 py-2.5">
            <span className="text-sm text-[#a3a3a3]">LOCAL_AI_API_KEY</span>
            <span
              data-testid="config-apikey-status"
              className={`font-mono text-xs ${config?.api_key_configured ? "text-emerald-400" : "text-[#737373]"}`}
            >
              {config?.api_key_configured ? "configurada (env)" : "vacía (no requerida)"}
            </span>
          </div>
          <p className="mt-2 text-xs text-[#737373]">
            La API key se define solo mediante variable de entorno en el backend y nunca se expone al navegador.
          </p>

          <div className="mt-4 flex items-start gap-2 rounded-sm border border-[#262626] bg-[#0a0a0a] px-3 py-2.5">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
            <div>
              <div className="text-sm text-[#d4d4d4]">Anti-SSRF · lista blanca de hosts</div>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="config-allowed-hosts">
                {(config?.allowed_hosts || []).map((h) => (
                  <span key={h} className="rounded-sm border border-[#404040] px-2 py-0.5 font-mono text-[11px] text-[#a3a3a3]">
                    {h}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-4 space-y-1.5 last:mb-0">
      <label className="font-mono text-xs uppercase tracking-wider text-[#a3a3a3]">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#737373]">{hint}</p>}
    </div>
  );
}
